import React, { useState, useRef, useEffect } from 'react';
import { Button } from './button';
import { Check, X, Edit, Loader2, AlertCircle, ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
}

interface InlineEditSelectProps {
  value: string;
  options: SelectOption[];
  onSave: (newValue: string) => Promise<void>;
  onCancel?: () => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

export const InlineEditSelect: React.FC<InlineEditSelectProps> = ({
  value,
  options,
  onSave,
  onCancel,
  label,
  className = '',
  disabled = false,
  required = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  // Update selected value when prop value changes
  useEffect(() => {
    if (!isEditing) {
      setSelectedValue(value);
    }
  }, [value, isEditing]);

  // Focus select when editing starts
  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus();
    }
  }, [isEditing]);

  const handleEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    setError(null);
    setSelectedValue(value);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedValue(value);
    setError(null);
    onCancel?.();
  };

  const handleSave = async () => {
    // Required validation
    if (required && !selectedValue) {
      setError('This field is required');
      return;
    }

    // No change, just exit editing mode
    if (selectedValue === value) {
      setIsEditing(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onSave(selectedValue);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const getDisplayLabel = (val: string): string => {
    const option = options.find(opt => opt.value === val);
    return option ? option.label : val;
  };

  if (!isEditing) {
    return (
      <div className={`group ${className}`}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          <div 
            className={`text-sm text-gray-900 bg-gray-50 rounded-md px-3 py-2 min-h-[40px] flex items-center justify-between cursor-pointer group-hover:bg-gray-100 transition-colors ${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            }`}
            onClick={handleEdit}
          >
            <span>{getDisplayLabel(value)}</span>
            <div className="flex items-center space-x-1">
              <ChevronDown className="w-4 h-4 text-gray-400" />
              {!disabled && (
                <Edit className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <select
            ref={selectRef}
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
            className={`flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="flex items-center space-x-1">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleSave}
              disabled={isLoading}
              className="px-2 py-1 h-8"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
              className="px-2 py-1 h-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {error && (
          <div className="flex items-center space-x-1 text-red-600 text-xs">
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};
